import { useEffect, useRef, useState } from "react";
import { useMap } from "react-leaflet";
import { leadHasMapCoordinates } from "../../utils/leadsMapInitialView";

const GPS_WAIT_MS = 10000;

/**
 * Last-resort camera: company HQ via setView.
 * Never remounts MapContainer. Skips if last view, lead fit, GPS, or mappable pins already won.
 */
export default function MapHqFallback({
  loading = false,
  leads = [],
  companyHqView = null,
  restoredLastViewRef,
  didFitLeadsRef,
  userZoomedRef,
  initialFitRef,
  gpsResolved = false,
}) {
  const map = useMap();
  const appliedRef = useRef(false);
  const [waitTimedOut, setWaitTimedOut] = useState(false);
  const hasMappable = Array.isArray(leads) && leads.some(leadHasMapCoordinates);

  useEffect(() => {
    if (
      loading ||
      hasMappable ||
      restoredLastViewRef?.current ||
      didFitLeadsRef?.current
    ) {
      return undefined;
    }
    const timer = setTimeout(() => setWaitTimedOut(true), GPS_WAIT_MS);
    return () => clearTimeout(timer);
  }, [loading, hasMappable, restoredLastViewRef, didFitLeadsRef]);

  useEffect(() => {
    if (appliedRef.current) return;
    if (loading) return;
    if (restoredLastViewRef?.current) return;
    if (didFitLeadsRef?.current) return;
    if (userZoomedRef?.current) return;
    if (hasMappable) return;
    if (!gpsResolved && !waitTimedOut) return;
    const center = companyHqView?.center;
    const zoom = Number(companyHqView?.zoom);
    if (!center || !Number.isFinite(zoom)) return;

    appliedRef.current = true;
    if (initialFitRef) initialFitRef.current = false;
    try {
      map.stop?.();
      map.setView(center, zoom, { animate: false });
    } catch {
      appliedRef.current = false;
    }
  }, [
    loading,
    hasMappable,
    companyHqView,
    gpsResolved,
    waitTimedOut,
    map,
    restoredLastViewRef,
    didFitLeadsRef,
    userZoomedRef,
    initialFitRef,
  ]);

  return null;
}
